/**
 * PricingPlans セクション。3 variants:
 *   tiered_3col       — Starter / Pro / Enterprise の 3 階層カード
 *   tiered_2col       — Standard / Premium の 2 階層
 *   single_highlight  — 1 つを大きく強調表示
 */
import { Check, DollarSign } from 'lucide-react';

import { ArrayField, TextField, TextareaField } from './primitives';
import type { EditorProps, RendererProps, SectionDefinition } from './types';

interface Plan {
  name: string;
  price: string;
  unit: string;
  description: string;
  features: string[];
  highlighted: boolean;
  ctaLabel: string;
  ctaHref: string;
}
interface PricingPlansConfig {
  title: string;
  subtitle: string;
  plans: Plan[];
}

function PricingEditor({ config, onChange, onCommit }: EditorProps) {
  const cfg = config as unknown as PricingPlansConfig;
  const set = (patch: Partial<PricingPlansConfig>) =>
    onChange({ ...config, ...patch });
  return (
    <div className="space-y-3">
      <TextField
        label="セクション見出し"
        value={cfg.title ?? ''}
        onChange={(v) => set({ title: v })}
        onCommit={onCommit}
      />
      <TextField
        label="サブタイトル"
        value={cfg.subtitle ?? ''}
        onChange={(v) => set({ subtitle: v })}
        onCommit={onCommit}
      />
      <ArrayField<Plan>
        label="プラン"
        items={cfg.plans ?? []}
        onChange={(next) => set({ plans: next })}
        emptyItem={() => ({
          name: '',
          price: '',
          unit: '/月',
          description: '',
          features: [],
          highlighted: false,
          ctaLabel: 'お問い合わせ',
          ctaHref: '/contact',
        })}
        renderItem={(item, _i, update) => (
          <div className="space-y-2">
            <TextField
              label="プラン名"
              value={item.name}
              onChange={(v) => update({ name: v })}
              onCommit={onCommit}
            />
            <div className="grid grid-cols-2 gap-2">
              <TextField
                label="価格"
                value={item.price}
                onChange={(v) => update({ price: v })}
                onCommit={onCommit}
                placeholder="¥9,800"
              />
              <TextField
                label="単位"
                value={item.unit}
                onChange={(v) => update({ unit: v })}
                onCommit={onCommit}
                placeholder="/月"
              />
            </div>
            <TextField
              label="説明"
              value={item.description}
              onChange={(v) => update({ description: v })}
              onCommit={onCommit}
            />
            <TextareaField
              label="含まれる機能 (1 行 = 1 機能)"
              value={(item.features ?? []).join('\n')}
              onChange={(v) =>
                update({ features: v.split('\n').filter((s) => s.trim() !== '') })
              }
              onCommit={onCommit}
              rows={4}
            />
            <label className="flex items-center gap-2 text-[11px]">
              <input
                type="checkbox"
                checked={item.highlighted}
                onChange={(e) => {
                  update({ highlighted: e.target.checked });
                  onCommit();
                }}
              />
              このプランを強調表示する
            </label>
            <div className="grid grid-cols-2 gap-2">
              <TextField
                label="CTA 文言"
                value={item.ctaLabel}
                onChange={(v) => update({ ctaLabel: v })}
                onCommit={onCommit}
              />
              <TextField
                label="CTA リンク"
                value={item.ctaHref}
                onChange={(v) => update({ ctaHref: v })}
                onCommit={onCommit}
              />
            </div>
          </div>
        )}
      />
    </div>
  );
}

function PlanCard({ plan, large }: { plan: Plan; large?: boolean }) {
  return (
    <div
      className={`relative rounded-xl border p-6 flex flex-col ${
        plan.highlighted
          ? 'border-[var(--accent)] border-2 bg-[var(--accent-subtle)]'
          : 'border-[var(--border)] bg-[var(--bg-elevated)]'
      } ${large ? 'p-8' : ''}`}
    >
      {plan.highlighted && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-[10px] font-bold bg-[var(--accent)] text-white">
          おすすめ
        </span>
      )}
      <div className="text-base font-semibold mb-1">{plan.name}</div>
      {plan.description && (
        <div className="text-xs text-[var(--text-muted)] mb-4">
          {plan.description}
        </div>
      )}
      <div className="mb-4">
        <span className={`font-bold ${large ? 'text-5xl' : 'text-3xl'}`}>
          {plan.price || '—'}
        </span>
        {plan.unit && (
          <span className="text-sm text-[var(--text-muted)] ml-1">
            {plan.unit}
          </span>
        )}
      </div>
      <ul className="space-y-2 mb-6 flex-1">
        {(plan.features ?? []).map((f, i) => (
          <li
            key={i}
            className="flex items-start gap-2 text-sm text-[var(--text-secondary)]"
          >
            <Check className="size-4 mt-0.5 text-[var(--accent-green)] flex-shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <a
        href={plan.ctaHref || '#'}
        className={`block text-center px-4 py-2.5 rounded font-medium text-sm ${
          plan.highlighted
            ? 'bg-[var(--accent)] text-white'
            : 'bg-[var(--bg-base)] border border-[var(--border)]'
        }`}
      >
        {plan.ctaLabel || 'お問い合わせ'}
      </a>
    </div>
  );
}

function makeRenderer(layout: 'tier3' | 'tier2' | 'single') {
  return function Render({ config }: RendererProps) {
    const v = config as unknown as PricingPlansConfig;
    const plans = v.plans ?? [];
    return (
      <section>
        {(v.title || v.subtitle) && (
          <div className="text-center mb-8">
            {v.title && <h3 className="text-2xl font-bold mb-1">{v.title}</h3>}
            {v.subtitle && (
              <p className="text-[var(--text-secondary)]">{v.subtitle}</p>
            )}
          </div>
        )}
        {layout === 'single' ? (
          <div className="max-w-md mx-auto">
            {plans[0] && <PlanCard plan={plans[0]} large />}
          </div>
        ) : (
          <div
            className={`grid gap-5 ${
              layout === 'tier3'
                ? 'grid-cols-1 md:grid-cols-3'
                : 'grid-cols-1 md:grid-cols-2'
            }`}
          >
            {plans.map((p, i) => (
              <PlanCard key={i} plan={p} />
            ))}
          </div>
        )}
      </section>
    );
  };
}

const samplePlan = (
  name: string,
  price: string,
  desc: string,
  features: string[],
  highlighted = false,
): Plan => ({
  name,
  price,
  unit: '/月',
  description: desc,
  features,
  highlighted,
  ctaLabel: 'お問い合わせ',
  ctaHref: '/contact',
});

export const pricingPlansDefinition: SectionDefinition = {
  type: 'pricing_plans',
  label: '料金プラン',
  icon: DollarSign,
  description: 'サブスク・利用料の階層別料金プランカード。',
  defaultVariant: 'tiered_3col',
  useCases: ['service', 'b2b_lease', 'platform'],
  variants: {
    tiered_3col: {
      label: '3 階層',
      description: 'Starter / Pro / Enterprise',
      Renderer: makeRenderer('tier3'),
      Editor: PricingEditor,
      sample: () => ({
        title: '料金プラン',
        subtitle: '事業規模に合わせた 3 つのプラン',
        plans: [
          samplePlan('Starter', '¥9,800', '個人事業主・少人数チーム向け', [
            'ユーザー 5 名まで',
            '基本機能',
            'メールサポート',
          ]),
          samplePlan(
            'Pro',
            '¥29,800',
            '成長中のチームに',
            [
              'ユーザー 25 名まで',
              '全機能',
              '優先サポート',
              'API アクセス',
            ],
            true,
          ),
          samplePlan('Enterprise', 'お見積', '大規模組織向けカスタマイズ', [
            'ユーザー無制限',
            'SAML SSO',
            '専任サポート',
            '監査ログ',
          ]),
        ],
      }),
    },
    tiered_2col: {
      label: '2 階層',
      description: 'Standard / Premium',
      Renderer: makeRenderer('tier2'),
      Editor: PricingEditor,
      sample: () => ({
        title: '料金プラン',
        subtitle: '',
        plans: [
          samplePlan('Standard', '¥19,800', '基本機能セット', [
            '基本機能すべて',
            'メールサポート',
          ]),
          samplePlan(
            'Premium',
            '¥49,800',
            'プロフェッショナル向け',
            ['Standard 全機能', '優先サポート', 'カスタムレポート'],
            true,
          ),
        ],
      }),
    },
    single_highlight: {
      label: '単一強調',
      description: '1 つのプランを大きく訴求',
      Renderer: makeRenderer('single'),
      Editor: PricingEditor,
      sample: () => ({
        title: 'シンプルな 1 プラン',
        subtitle: 'すべての機能をひとつの価格で',
        plans: [
          samplePlan(
            'All-in-One',
            '¥39,800',
            'すべての機能と無制限利用',
            ['ユーザー無制限', '全機能利用可', '24/7 サポート'],
            true,
          ),
        ],
      }),
    },
  },
};
